import { motion, useReducedMotion } from 'framer-motion';
import { Play, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

const EASE = [0.22, 1, 0.36, 1] as const;

interface Stat {
    value: string;
    label: string;
}

interface BookingItem {
    icon: LucideIcon;
    label: string;
    /** Optional pill (e.g. "New", "Soon"). */
    badge?: string;
}

interface DemoVideoStripProps {
    /** CDN URL of the web-optimized demo mp4. Empty string = placeholder mode. */
    videoUrl: string;
    /** Optional poster frame URL. When absent, a branded gradient stands in. */
    posterUrl?: string;
    /** Eyebrow (tracked caps) above the heading. */
    eyebrow?: string;
    /** Didot heading. */
    heading?: string;
    /** Standfirst beneath the heading. */
    subtitle?: string;
    /** Headline metrics shown in a bar under the player. */
    stats?: Stat[];
    /** "What you book in FastBook" icon row. */
    bookingItems?: BookingItem[];
    /** Caption above the booking-item row. */
    bookingCaption?: string;
}

/**
 * Demo video strip for /become-an-agent. Ivory band framing a 16:9 player,
 * with an optional stat bar and FastBook capability row beneath it.
 *
 * Click-to-play from a poster: the <video> only mounts (and only fetches) once
 * the play button is pressed, so the transcoded file never loads on page view.
 */
export default function DemoVideoStrip({
    videoUrl,
    posterUrl,
    eyebrow = 'See It In Action',
    heading = 'See the platform in action.',
    subtitle,
    stats,
    bookingItems,
    bookingCaption = 'What you book in FastBook',
}: DemoVideoStripProps) {
    const shouldReduceMotion = useReducedMotion();
    const [playing, setPlaying] = useState(false);

    const canPlay = videoUrl.trim() !== '';

    return (
        <section id="demo" className="agent-px scroll-mt-36 bg-[#f7f4ec] py-20 sm:py-28">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-12 max-w-3xl"
                >
                    <span className="lux-caps lux-caps-lg mb-4 block text-[#0e2727]/50">{eyebrow}</span>
                    <h2 className="lux-didot lux-didot-lg text-[#0e2727]">{heading}</h2>
                    {subtitle && <p className="lux-body lux-body-lg mt-5 text-[#0e2727]/70">{subtitle}</p>}
                </motion.div>

                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="relative aspect-video overflow-hidden rounded-2xl bg-[#0b2222] shadow-2xl ring-1 ring-[#0e2727]/10"
                >
                    {playing && canPlay ? (
                        <video
                            className="h-full w-full"
                            src={videoUrl}
                            poster={posterUrl}
                            controls
                            autoPlay
                            playsInline
                            preload="none"
                        >
                            <track kind="captions" />
                        </video>
                    ) : (
                        <>
                            {posterUrl ? (
                                <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
                            ) : (
                                // Branded placeholder until the real poster lands.
                                <div className="absolute inset-0 bg-gradient-to-br from-[#0e2727] to-[#0b2222]" />
                            )}

                            <button
                                type="button"
                                onClick={() => canPlay && setPlaying(true)}
                                disabled={!canPlay}
                                aria-label={canPlay ? 'Play demo video' : 'Demo video coming soon'}
                                className="group absolute inset-0 flex items-center justify-center focus:outline-none disabled:cursor-default"
                            >
                                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#f7f4ec]/95 shadow-lg ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-105 group-focus-visible:scale-105">
                                    <Play className="ml-1 h-8 w-8 fill-[#0e2727] text-[#0e2727]" strokeWidth={1.5} />
                                </span>
                            </button>
                        </>
                    )}
                </motion.div>

                {stats && stats.length > 0 && (
                    <motion.dl
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                        className="mt-12 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-[#0e2727]/10 pt-12 md:grid-cols-4"
                    >
                        {stats.map((s) => (
                            <div key={s.label}>
                                <dt className="lux-didot text-4xl text-[#0e2727] sm:text-5xl">{s.value}</dt>
                                <dd className="lux-caps mt-3 text-xs leading-relaxed text-[#0e2727]/55">{s.label}</dd>
                            </div>
                        ))}
                    </motion.dl>
                )}

                {bookingItems && bookingItems.length > 0 && (
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                        className="mt-14"
                    >
                        <span className="lux-caps lux-caps-lg mb-7 block text-[#0e2727]/45">{bookingCaption}</span>
                        <ul className="flex flex-wrap gap-x-10 gap-y-7">
                            {bookingItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <li key={item.label} className="flex items-center gap-3">
                                        <Icon className="h-6 w-6 shrink-0 text-[#0e2727]/70" strokeWidth={1.5} />
                                        <span className="lux-body lux-body-sm font-medium text-[#0e2727]/85">{item.label}</span>
                                        {item.badge && (
                                            <span className="lux-caps rounded-full bg-[#0e2727] px-2 py-0.5 text-[10px] text-[#f7f4ec]">
                                                {item.badge}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
